/**
 * Enterprise-grade SweetAlert2 Configuration
 * Beautiful, animated alerts with consistent styling
 */

import Swal from 'sweetalert2'

// Custom class configurations for different alert types
const customClasses = {
  popup: 'swal-popup-custom',
  title: 'swal-title-custom',
  htmlContainer: 'swal-html-custom',
  confirmButton: 'swal-confirm-btn',
  cancelButton: 'swal-cancel-btn',
  denyButton: 'swal-deny-btn',
  input: 'swal-input-custom',
  actions: 'swal-actions-custom',
  icon: 'swal-icon-custom'
}

// Base configuration for all alerts
const baseConfig = {
  customClass: customClasses,
  buttonsStyling: false,
  showClass: {
    popup: 'swal-animate-in'
  },
  hideClass: {
    popup: 'swal-animate-out'
  },
  backdrop: 'rgba(0, 0, 0, 0.5)',
  allowOutsideClick: false,
  allowEscapeKey: true,
  focusConfirm: false,
  reverseButtons: true
}

// Pre-configured alert types
const alertPresets = {
  // Success alert
  success: {
    ...baseConfig,
    icon: 'success',
    confirmButtonText: 'Done',
    timer: 3000,
    timerProgressBar: true
  },
  
  // Error alert
  error: {
    ...baseConfig,
    icon: 'error',
    confirmButtonText: 'OK'
  },
  
  // Warning alert
  warning: {
    ...baseConfig,
    icon: 'warning',
    confirmButtonText: 'OK'
  },
  
  // Info alert
  info: {
    ...baseConfig,
    icon: 'info',
    confirmButtonText: 'Got it'
  },
  
  // Confirmation dialog
  confirm: {
    ...baseConfig,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, proceed',
    cancelButtonText: 'Cancel'
  },
  
  // Delete confirmation
  delete: {
    ...baseConfig,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete',
    cancelButtonText: 'Cancel',
    customClass: {
      ...customClasses,
      confirmButton: 'swal-confirm-btn swal-btn-danger'
    }
  },
  
  // Input dialog
  input: {
    ...baseConfig,
    showCancelButton: true,
    confirmButtonText: 'Submit',
    cancelButtonText: 'Cancel',
    inputAutoFocus: true
  },
  
  // Loading state
  loading: {
    ...baseConfig,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading()
    }
  }
}

/**
 * Enhanced SweetAlert wrapper with enterprise features
 */
const Alert = {
  /**
   * Show a success alert
   */
  success(title, text = '', options = {}) {
    return Swal.fire({
      ...alertPresets.success,
      title,
      text,
      ...options
    })
  },
  
  /**
   * Show an error alert
   */
  error(title, text = '', options = {}) {
    return Swal.fire({
      ...alertPresets.error,
      title,
      text,
      ...options
    })
  },
  
  /**
   * Show a warning alert
   */
  warning(title, text = '', options = {}) {
    return Swal.fire({
      ...alertPresets.warning,
      title,
      text,
      ...options
    })
  },
  
  /**
   * Show an info alert
   */
  info(title, text = '', options = {}) {
    return Swal.fire({
      ...alertPresets.info,
      title,
      text,
      ...options
    })
  },
  
  /**
   * Show a confirmation dialog
   */
  confirm(title, text = '', options = {}) {
    return Swal.fire({
      ...alertPresets.confirm,
      title,
      text,
      ...options
    })
  },
  
  /**
   * Show a delete confirmation dialog
   */
  delete(title, text = '', options = {}) {
    return Swal.fire({
      ...alertPresets.delete,
      title,
      text,
      ...options
    })
  },
  
  /**
   * Show an input dialog
   */
  input(title, options = {}) {
    return Swal.fire({
      ...alertPresets.input,
      title,
      input: options.inputType || 'text',
      inputPlaceholder: options.placeholder || '',
      inputValue: options.defaultValue || '',
      ...options
    })
  },
  
  /**
   * Show a textarea input dialog
   */
  textarea(title, options = {}) {
    return Swal.fire({
      ...alertPresets.input,
      title,
      input: 'textarea',
      inputPlaceholder: options.placeholder || 'Enter your message...',
      inputValue: options.defaultValue || '',
      ...options
    })
  },
  
  /**
   * Show a loading indicator
   */
  loading(title = 'Please wait...', text = '') {
    return Swal.fire({
      ...alertPresets.loading,
      title,
      text
    })
  },
  
  /**
   * Close any open alert
   */
  close() {
    Swal.close()
  },
  
  /**
   * Show loading on current alert
   */
  showLoading() {
    Swal.showLoading()
  },
  
  /**
   * Hide loading on current alert
   */
  hideLoading() {
    Swal.hideLoading()
  },
  
  /**
   * Update current alert
   */
  update(options) {
    Swal.update(options)
  },
  
  /**
   * Promise-based operation with loading state
   */
  async withLoading(promise, { loading = 'Processing...', success, error } = {}) {
    this.loading(loading)
    
    try {
      const result = await promise
      Swal.close()
      if (success) {
        await this.success(
          typeof success === 'function' ? success(result) : success
        )
      }
      return result
    } catch (err) {
      Swal.close()
      if (error) {
        await this.error(
          typeof error === 'function' ? error(err) : error,
          err.message || ''
        )
      }
      throw err
    }
  },
  
  /**
   * Custom alert with full options
   */
  custom(options) {
    return Swal.fire({
      ...baseConfig,
      ...options
    })
  },
  
  /**
   * Access to raw Swal for advanced usage
   */
  raw: Swal
}

// Vue plugin
export const AlertPlugin = {
  install(app) {
    app.config.globalProperties.$alert = Alert
    app.provide('alert', Alert)
  }
}

// Export for composition API
export function useAlert() {
  return Alert
}

export default Alert
